import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useDashboards from '../hooks/useDashboards'
import useSnackbar from '../hooks/useSnackbar'
import { DashboardConfig } from '../types/gotypes'
import DashboardLayoutBuilder from '../components/dashboard/builder/DashboardLayoutBuilder'
import { getSystemTemplate } from '../components/dashboard/builder/systemTemplates'
import { stampTemplateJob } from '../utils/dashboardBuilderLayout'
import { GENERATED_CONFIG_STORAGE_KEY } from '../hooks/useGenerateDashboardStructure'

function blankConfig(name: string, firstPageName: string): DashboardConfig {
  return {
    name,
    description: '',
    layout: { rows: [] },
    pages: [{ id: crypto.randomUUID(), name: firstPageName || 'Page 1', layout: { rows: [] } }],
  }
}

function configHasAnyWidget(config: DashboardConfig): boolean {
  const pages = config.pages && config.pages.length > 0 ? config.pages : [{ layout: config.layout }]
  return pages.some(p => (p.layout?.rows || []).some(r => r.cells.length > 0))
}

/**
 * The layout builder page (#971 Phase 1) — the single destination all three
 * creation-screen entry points converge on. Reached from `DashboardCreatePage`
 * with the chosen name (and optionally a system template id) as query params.
 *
 * "Create Dashboard" persists the structure and hands off to the existing
 * `DashboardEditor` — the same component every other dashboard is edited
 * through — so widget configuration reuses its Edit Cell flow unchanged.
 */
const DashboardBuilderPage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const snackbar = useSnackbar()
  const customer = router.params.customer || account.customer?.name || ''
  const job = account.job?.name || ''
  const nameParam = router.params.name || 'New Dashboard'
  // #1182: the first page's name, asked for on the create screen's naming step.
  const pageNameParam = router.params.pageName || 'Page 1'
  const templateId = router.params.template
  // A personal/company template (#971 Phase 2) — a stored dashboard id, as
  // opposed to `template`'s hardcoded system-template id. Its config arrives
  // async (a GET), unlike a system template's synchronous `build(job)`.
  const templateDashboardId = router.params.templateDashboardId
  // An agent-generated structure (#971 Phase 3) — the config was stashed in
  // sessionStorage by DashboardCreatePage (it doesn't fit in a query param)
  // and this flag says to read it. Seeds synchronously in the state
  // initializer below, exactly like a system template's build(job).
  const generatedParam = Boolean(router.params.generated)

  const { createDashboard, dashboard: sourceTemplate, dashboardIsLoading: sourceTemplateLoading } = useDashboards({
    customer,
    dashboardId: templateDashboardId,
  })

  const [config, setConfig] = useState<DashboardConfig>(() => {
    if (generatedParam) {
      const raw = sessionStorage.getItem(GENERATED_CONFIG_STORAGE_KEY)
      if (raw) {
        try {
          const generated = JSON.parse(raw) as DashboardConfig
          return { ...generated, name: nameParam }
        } catch {
          // Unreadable stash — fall through to a blank canvas rather than dying.
        }
      }
    }
    if (templateId) {
      const template = getSystemTemplate(templateId)
      if (template) {
        const built = template.build(job)
        return { ...built, name: nameParam }
      }
    }
    return blankConfig(nameParam, pageNameParam)
  })
  // A stored template's config isn't available synchronously, so it seeds the
  // builder once the fetch resolves — the React-blessed "adjust state during
  // render" pattern (comparing against a one-shot guard), not a useEffect:
  // there's no cleanup here, just a one-time initialization from fetched data.
  const [seededFromStoredTemplate, setSeededFromStoredTemplate] = useState(false)
  if (templateDashboardId && sourceTemplate?.config && !seededFromStoredTemplate) {
    setSeededFromStoredTemplate(true)
    setConfig({ ...stampTemplateJob(sourceTemplate.config, job), name: nameParam })
  }

  const [creating, setCreating] = useState(false)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)

  const isManager = Boolean(account.user?.role_permissions?.isManager)

  const handleExit = () => {
    if (configHasAnyWidget(config)) {
      setDiscardConfirmOpen(true)
      return
    }
    router.navigate('dashboards', { customer })
  }

  const confirmDiscard = () => {
    setDiscardConfirmOpen(false)
    router.navigate('dashboards', { customer })
  }

  const handleCreate = async () => {
    if (!job) {
      snackbar.error('Open a job in your session before creating a dashboard.')
      return
    }
    setCreating(true)
    try {
      const created = await createDashboard(config, job)
      if (created) {
        router.navigate('dashboard-editor', { customer, dashboardId: created.id })
      }
    } finally {
      setCreating(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={handleExit} size="small">Cancel</Button>
        <Typography variant="h6">{config.name || 'New Dashboard'}</Typography>
        <Button
          variant="contained"
          color="info"
          onClick={handleCreate}
          disabled={creating || !job}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Create Dashboard
        </Button>
      </Box>

      {!job ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">Open a job in your session before creating a dashboard.</Alert>
        </Box>
      ) : templateDashboardId && sourceTemplateLoading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DashboardLayoutBuilder config={config} onConfigChange={setConfig} job={job} isManager={isManager} />
        </Box>
      )}

      <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)}>
        <DialogTitle>Discard this layout?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've placed widgets on the canvas. Leaving now discards them — nothing has been saved yet.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardConfirmOpen(false)}>Keep editing</Button>
          <Button onClick={confirmDiscard} color="error">Discard</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DashboardBuilderPage
